import _ from 'lodash';
import React, { useEffect, useState } from 'react';

import { styled } from 'styled-components';

import { ReactComponent as MapPin } from '../../../../../assets/map-pin.svg';
import EmptySearch from './EmptySearch';
import Highlight from './Highlight';

import { api } from 'common';
import { StyledTypography } from '..';
import { Location } from '../../Search';
import { Results, StyledTextField } from './SpecialtyProviderSearch';
import { Result } from './SearchResultItem';

interface Props {
  locationIconColor: string;
  ipLocation?: api.IpStack.Response;
  locationSearch?: Location;
  getPlaceSuggestions: (input: string) => Promise<api.Places.PlaceSuggestion[]>;
  getPlaceDetails: (
    placeId?: string,
  ) => Promise<api.Places.PlaceDetails | undefined>;
  onLocationChange: (location?: Location) => void;
  onFocus?: () => void;
  onBlur?: () => void;
}

const LocationSearch: React.FC<Props> = ({
  locationIconColor,
  ipLocation,
  locationSearch,
  getPlaceSuggestions,
  getPlaceDetails,
  onLocationChange,
  onFocus,
  onBlur,
}) => {
  const [inputValue, setInputValue] = useState<string>('');
  const [searchItems, setSearchItems] = useState<api.Places.PlaceSuggestion[]>(
    [],
  );
  const [focus, setFocus] = useState<boolean>(false);
  const [location, setLocation] = useState<api.Places.PlaceSuggestion>();

  useEffect(() => {
    if (ipLocation) {
      setLocation({
        mainText: ipLocation?.city,
        secondaryText: `${ipLocation?.region_code} ${ipLocation?.zip}`,
        placeId: '',
      });
    }

    if (locationSearch && !ipLocation) {
      setLocation({
        mainText: locationSearch.city,
        secondaryText: `${locationSearch.region_code} ${locationSearch.zip}`,
        placeId: '',
      });
    }
  }, [ipLocation, locationSearch]);

  const handleItemClick = async (item: api.Places.PlaceSuggestion) => {
    setFocus(false);
    setLocation(item);
    const details = await getPlaceDetails(item.placeId);
    if (!details?.latitude || !details?.longitude) {
      return;
    }
    onLocationChange({
      latitude: details.latitude,
      longitude: details.longitude,
      region_code: details.state,
      zip: details.zipCode,
      city: details.city,
    });
    setInputValue('');
  };

  const itemResults = () => {
    const itemCount = searchItems.length;
    if (itemCount === 0) {
      return null;
    }
    return (
      <div className="py-3">
        {searchItems.map((item) => {
          const startIndex = item.mainText
            .toLowerCase()
            .indexOf(inputValue.toLowerCase());
          const endIndex = startIndex + inputValue.length;
          return (
            <Result
              onMouseDown={() => {
                handleItemClick(item);
              }}
              key={item.mainText + item.secondaryText}
              tabIndex={0}
            >
              <StyledTypography
                variant="title-medium"
                color={'var(--color-shade-80)'}
              >
                {item.mainText.substring(0, startIndex)}
                <Highlight
                  text={item.mainText.substring(startIndex, endIndex)}
                />
                {item.mainText.substring(endIndex, item.mainText.length)}
                {` `}
                {item.secondaryText}
              </StyledTypography>
            </Result>
          );
        })}
      </div>
    );
  };

  const updateInputValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.currentTarget.value;
    setLocation(undefined);
    if (newValue.length > 0) {
      const debounceFilter = _.debounce(async () => {
        const suggestedItems = await getPlaceSuggestions(newValue);
        setSearchItems(suggestedItems);
      }, 150);
      debounceFilter();
    }
    setInputValue(newValue);
    if (newValue === '') {
      setSearchItems([]);
    }
  };

  const results = () => {
    if (!focus) {
      return null;
    }
    if (inputValue !== '' && searchItems.length > 0) {
      return (
        <Results>
          <ResultsContainer>{itemResults()}</ResultsContainer>
        </Results>
      );
    } else if (searchItems.length === 0 && inputValue.length > 0) {
      return (
        <Results>
          <EmptySearch inputValue={inputValue} />
        </Results>
      );
    }
    return null;
  };

  return (
    <>
      <SearchContainer>
        <StyledMapIcon color={locationIconColor} />
        <StyledTextField
          placeholder={`Location`}
          value={
            location?.mainText && location.secondaryText
              ? location.mainText + ' ' + location.secondaryText
              : inputValue
          }
          onMouseDown={() => {
            setLocation(undefined);
            setSearchItems([]);
            setInputValue('');
            onLocationChange(undefined);
          }}
          defaultValue={
            ipLocation && !Object.values(ipLocation).some((v) => !v || v === 0)
              ? `${ipLocation?.city}, ${ipLocation?.region_code} ${ipLocation?.zip}`
              : undefined
          }
          onFocus={() => {
            if (onFocus) {
              onFocus();
            }
            setFocus(true);
          }}
          onBlur={() => {
            if (onBlur) {
              onBlur();
            }
            setFocus(false);
          }}
          inputMode="text"
          onChange={(event) => updateInputValue(event)}
        />
      </SearchContainer>
      {focus ? results() : null}
    </>
  );
};

export default LocationSearch;

export const SearchContainer = styled.div`
  display: flex;
  padding: 0 0 0 0.75rem;
  height: 48px;
  align-items: center;
`;

const StyledMapIcon = styled(MapPin).attrs<{ color: string }>((props) => ({
  color: props.color || 'var(--color-shade-30)',
}))`
  stroke-width: 1;
  color: ${(props) => props.color};
`;

const ResultsContainer = styled.div`
  width: 100%;
  padding-bottom: 24px;
`;
